'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  MessageSquare,
  ThumbsUp,
  CheckCircle2,
  Pin
} from 'lucide-react';
import { Badge } from '#/components/ui/badge';
import { formatDateKST } from '#/lib/date';

interface QaCategory {
  id: string;
  name: string;
  slug: string;
  order: number;
  /** 다른 탭과 글이 겹치는 묶음 탭(띄어쓰기). 전체 건수 합계에서 뺀다(#313). */
  overlay?: boolean;
  _count?: { questions: number };
  children?: (QaCategory & { _count?: { questions: number } })[];
}

interface QaQuestionItem {
  id: string;
  name: string;
  slug: string | null;
  summary: string | null;
  text: string;
  viewCount: number;
  answerCount: number;
  upvoteCount: number;
  pinned: boolean;
  published: boolean;
  tags: string[];
  subTags: string[];
  ruleTags: string[];
  createdAt: string;
  acceptedAnswerId: string | null;
  category: {
    id: string;
    name: string;
    slug: string;
    parent?: { id: string; name: string; slug: string } | null;
  } | null;
  _count?: { answers: number };
}

interface QaListProps {
  categories: QaCategory[];
  questions: QaQuestionItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  currentCategory: string;
  currentSearch: string;
  userId: string | null;
  /** 공개된 글 전체 수 (탭 건수의 합이 아니다) */
  totalCount: number;
}

export default function QaList({
  categories,
  questions,
  pagination,
  currentCategory,
  currentSearch,
  userId,
  totalCount
}: QaListProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchInput, setSearchInput] = useState(currentSearch);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  const checkScrollable = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const hasOverflow = container.scrollWidth > container.clientWidth;
    const isAtStart = container.scrollLeft <= 0;
    const isAtEnd =
      container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
    setShowLeftScroll(hasOverflow && !isAtStart);
    setShowRightScroll(hasOverflow && !isAtEnd);
  };

  useEffect(() => {
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, [categories]);

  const scrollToDirection = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const scrollAmount = 200;
    container.scrollTo({
      left:
        direction === 'left'
          ? container.scrollLeft - scrollAmount
          : container.scrollLeft + scrollAmount,
      behavior: 'smooth'
    });
  };

  // 전체 건수는 따로 센 값을 쓴다. 띄어쓰기 탭과 이어진 중분류(형태·활용·어미)는
  // 여러 탭에 같은 글이 겹쳐 보여 탭 건수를 더하면 두 번 센다(#313).
  const totalQuestionsCount = totalCount;

  // URL 업데이트
  const updateURL = (updates: Record<string, string | undefined>) => {
    const params = new URLSearchParams(searchParams.toString());
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    if (params.get('page') === '1') params.delete('page');
    if (params.get('category') === 'all') params.delete('category');
    const qs = params.toString();
    router.push(`/qa${qs ? `?${qs}` : ''}`, { scroll: false });
  };

  // 지금 고른 대분류. 중분류를 골랐으면 그 부모다. 아래 중분류 칩 줄에 쓴다(#313).
  const activeMajor = categories.find(
    (cat) =>
      cat.slug === currentCategory ||
      cat.children?.some((c) => c.slug === currentCategory)
  );
  const subCategories = (activeMajor?.children ?? [])
    .filter((c) => (c._count?.questions || 0) > 0)
    .sort((a, b) => (b._count?.questions || 0) - (a._count?.questions || 0));

  const handleCategoryChange = (slug: string) => {
    updateURL({ category: slug === 'all' ? undefined : slug, page: undefined });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateURL({ search: searchInput.trim() || undefined, page: undefined });
  };

  const handlePageChange = (newPage: number) => {
    updateURL({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 드래그 스크롤
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    scrollContainerRef.current.scrollLeft = scrollLeft - (x - startX) * 2;
  };
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  // 텍스트 잘라내기
  const truncate = (text: string, max: number) =>
    text.length > max ? text.slice(0, max) + '…' : text;

  return (
    <div>
      {/* 검색 바 + 질문하기 버튼 */}
      <div className="mb-6 flex gap-3">
        <form onSubmit={handleSearchSubmit} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="질문, 답변, 태그로 검색..."
            className="w-full pl-10 pr-4 py-3 border border-[#104573]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#104573] focus:border-transparent"
          />
        </form>
        {/* <QaCreateQuestionDialog categories={categories} userId={userId} /> */}
      </div>

      {/* 카테고리 탭 */}
      <div className="mb-6 relative">
        {showLeftScroll && (
          <button
            onClick={() => scrollToDirection('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-background/90 hover:bg-background shadow-md rounded-full p-1.5 transition-all"
            aria-label="왼쪽으로 스크롤"
          >
            <ChevronLeft className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
        {showRightScroll && (
          <button
            onClick={() => scrollToDirection('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-background/90 hover:bg-background shadow-md rounded-full p-1.5 transition-all"
            aria-label="오른쪽으로 스크롤"
          >
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onScroll={checkScrollable}
          className="border-b border-[#104573]/10 overflow-x-auto scrollbar-hide"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
        >
          <div className="flex space-x-1 min-w-max">
            <button
              onClick={() => handleCategoryChange('all')}
              className={`px-4 py-2 font-medium whitespace-nowrap transition-colors select-none ${
                currentCategory === 'all'
                  ? 'text-[#104573] border-b-2 border-[#104573]'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              전체 ({totalQuestionsCount})
            </button>
            {categories.map((cat) => {
              const count =
                (cat._count?.questions || 0) +
                (cat.children?.reduce(
                  (s, c) => s + (c._count?.questions || 0),
                  0
                ) || 0);
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 font-medium whitespace-nowrap transition-colors select-none ${
                    activeMajor?.slug === cat.slug
                      ? 'text-[#104573] border-b-2 border-[#104573]'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {cat.name} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {showLeftScroll && (
          <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white via-white/50 to-transparent pointer-events-none" />
        )}
        {showRightScroll && (
          <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white via-white/50 to-transparent pointer-events-none" />
        )}
      </div>

      {/* 중분류 칩: 대분류 안에서 다른 주제로 옮겨 가게 한다(#313) */}
      {subCategories.length > 0 && activeMajor && (
        <div className="-mt-3 mb-6 flex flex-wrap gap-1.5">
          <button
            onClick={() => handleCategoryChange(activeMajor.slug)}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${
              currentCategory === activeMajor.slug
                ? 'bg-[#104573] text-white'
                : 'bg-[#104573]/10 text-[#0c3558] dark:text-[#60a3d9] hover:bg-[#104573]/20'
            }`}
          >
            {activeMajor.name} 전체
          </button>
          {subCategories.map((c) => (
            <button
              key={c.id}
              onClick={() => handleCategoryChange(c.slug)}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                currentCategory === c.slug
                  ? 'bg-[#104573] text-white'
                  : 'bg-[#104573]/10 text-[#0c3558] dark:text-[#60a3d9] hover:bg-[#104573]/20'
              }`}
            >
              {c.name} {c._count?.questions || 0}
            </button>
          ))}
        </div>
      )}

      {/* 질문 리스트 */}
      {questions.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-muted-foreground">
            {currentSearch
              ? `"${currentSearch}"에 대한 검색 결과가 없습니다.`
              : '등록된 질문이 없습니다.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((q) => (
            <Link key={q.id} href={`/qa/${q.id}`} className="block group">
              <div className="border border-[#104573]/10 rounded-lg p-5 hover:border-[#104573]/30 hover:shadow-sm transition-all bg-card">
                {/* 상단: 카테고리 배지 + 태그 */}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {q.pinned && (
                    <Badge
                      variant="destructive"
                      className="text-xs flex items-center gap-1"
                    >
                      <Pin className="w-3 h-3" />
                      고정
                    </Badge>
                  )}
                  {q.category && (
                    <Badge variant="secondary" className="text-xs">
                      {q.category.parent
                        ? `${q.category.parent.name} > ${q.category.name}`
                        : q.category.name}
                    </Badge>
                  )}
                  {q.acceptedAnswerId && (
                    <Badge className="text-xs bg-green-100 text-green-800 hover:bg-green-200 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      답변 채택됨
                    </Badge>
                  )}
                </div>

                {/* 질문 제목 */}
                <h3 className="text-lg font-semibold text-foreground group-hover:text-[#104573] transition-colors mb-1">
                  {q.name}
                </h3>

                {/* 요약 또는 본문 미리보기 */}
                {(q.summary || q.text) && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {q.summary || truncate(q.text.replace(/<[^>]*>/g, ''), 120)}
                  </p>
                )}

                {/* 태그 목록 */}
                {q.tags.length > 0 && (
                  <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                    {q.tags.slice(0, 5).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-0.5 bg-[#104573]/10 text-[#104573]/70 rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                    {q.tags.length > 5 && (
                      <span className="text-xs text-muted-foreground">
                        +{q.tags.length - 5}
                      </span>
                    )}
                  </div>
                )}

                {/* 하단: 메타정보 */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3.5 h-3.5" />
                    {q.viewCount.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5" />
                    {q.answerCount}
                  </span>
                  <span className="flex items-center gap-1">
                    <ThumbsUp className="w-3.5 h-3.5" />
                    {q.upvoteCount}
                  </span>
                  <span className="ml-auto">
                    {formatDateKST(q.createdAt, 'short')}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* 페이지네이션 */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="px-3 py-2 text-sm border border-[#104573]/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#104573]/[0.03] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
            .filter((p) => {
              const current = pagination.page;
              return (
                p === 1 ||
                p === pagination.totalPages ||
                Math.abs(p - current) <= 2
              );
            })
            .reduce<(number | 'ellipsis')[]>((acc, p, i, arr) => {
              if (i > 0 && p - (arr[i - 1] as number) > 1) {
                acc.push('ellipsis');
              }
              acc.push(p);
              return acc;
            }, [])
            .map((item, idx) =>
              item === 'ellipsis' ? (
                <span key={`e-${idx}`} className="px-2 text-muted-foreground">
                  …
                </span>
              ) : (
                <button
                  key={item}
                  onClick={() => handlePageChange(item as number)}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                    pagination.page === item
                      ? 'bg-[#104573] text-white'
                      : 'border border-[#104573]/20 hover:bg-[#104573]/[0.03]'
                  }`}
                >
                  {item}
                </button>
              )
            )}

          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className="px-3 py-2 text-sm border border-[#104573]/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#104573]/[0.03] transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 결과 정보 */}
      <div className="text-center text-sm text-muted-foreground mt-4 mb-8">
        총 {pagination.total.toLocaleString()}개의 질문 중{' '}
        {(pagination.page - 1) * pagination.limit + 1}-
        {Math.min(pagination.page * pagination.limit, pagination.total)}번째
      </div>
    </div>
  );
}
